import type { TFunction } from 'i18next';
import { logger } from '@/shared/lib/logger';
import { synthesizeSpeech } from '@/features/alpha/alpha.service';
import { resolveResponseBody, type ResponseBody } from '@/features/alpha/responseBody';

/**
 * Read-aloud for Conversational AI answers. Output only: it voices an answer the
 * chat has ALREADY received from its single POST /query and rendered. It never
 * starts a turn.
 *
 * Two voices, one at a time:
 *   1. AiDE text-to-speech through Conv-AI (synthesizeSpeech → /voice/speak) —
 *      the production voice, reading the text word for word.
 *   2. The browser's own SpeechSynthesis — used only when (1) fails, e.g. before
 *      the backend is deployed, or when audio playback is refused.
 *
 * AiDE audio is played with Web Audio (decodeAudioData + a buffer source), not an
 * <audio src="blob:…">: the page's Content-Security-Policy has no media-src, so
 * default-src 'self' blocks blob: media, and widening the CSP is not needed for this.
 *
 * stopReadAloud() silences both, abandons a synthesis request still in flight,
 * and makes any late result from it inert. Nothing here throws, so text chat
 * never depends on it.
 */

type Translate = TFunction;

/** How many KPI / ranking lines are spoken before "and N more". */
const MAX_SPOKEN_ITEMS = 5;
/** How many fields of a single-record table are spoken. */
const MAX_SPOKEN_FIELDS = 8;

const synth = (): SpeechSynthesis | null =>
  typeof window !== 'undefined' &&
  'speechSynthesis' in window &&
  typeof window.SpeechSynthesisUtterance === 'function'
    ? window.speechSynthesis
    : null;

export const isReadAloudSupported = (): boolean => synth() !== null;

/** Bumped by every stop/start, so a superseded request can tell it is stale. */
let generation = 0;
let pending: AbortController | null = null;
let playing: { context: AudioContext; source: AudioBufferSourceNode | null } | null = null;

const releasePlayback = (): void => {
  if (!playing) return;
  const { context, source } = playing;
  playing = null;
  try {
    if (source) {
      source.onended = null;
      source.stop();
    }
  } catch {
    // Already stopped.
  }
  context.close().catch(() => undefined);
};

const audioContextClass = (): typeof AudioContext | null =>
  typeof window !== 'undefined'
    ? window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext ??
      null
    : null;

/** Stop whatever the assistant is saying now. Safe to call at any time. */
export const stopReadAloud = (): void => {
  generation += 1;
  pending?.abort();
  pending = null;
  releasePlayback();
  try {
    synth()?.cancel();
  } catch (err) {
    logger.warn('Read-aloud cancel failed:', err);
  }
};

const speakWithBrowser = (text: string, lang?: string): boolean => {
  const speech = synth();
  if (!speech) return false;
  try {
    speech.cancel();
    const utterance = new window.SpeechSynthesisUtterance(text);
    if (lang) utterance.lang = lang;
    speech.speak(utterance);
    return true;
  } catch (err) {
    logger.warn('Read-aloud failed:', err);
    return false;
  }
};

export type ReadAloudVoice = 'aide' | 'browser' | 'none';

/**
 * Speak `text`, replacing anything still being spoken — audio never overlaps.
 * Resolves with the voice that spoke it ('none' when nothing was, including
 * when it was stopped or superseded first).
 */
export const readAloud = async (
  text: string,
  options: { lang?: string | undefined; convUiBaseUrl?: string | undefined } = {},
): Promise<ReadAloudVoice> => {
  stopReadAloud();
  const spoken = text.trim();
  if (!spoken) return 'none';

  const mine = generation;
  const controller = new AbortController();
  pending = controller;

  try {
    const clip = await synthesizeSpeech(spoken, options.convUiBaseUrl, controller.signal);
    if (mine !== generation) return 'none';

    const Context = audioContextClass();
    if (!Context) throw new Error('Web Audio is unavailable');
    const context = new Context();
    const current = { context, source: null as AudioBufferSourceNode | null };
    playing = current;

    const buffer = await context.decodeAudioData(clip);
    if (mine !== generation) return 'none';
    if (context.state === 'suspended') await context.resume();

    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(context.destination);
    source.onended = () => {
      if (playing === current) releasePlayback();
    };
    current.source = source;
    source.start();
    return 'aide';
  } catch (err) {
    if (mine !== generation) return 'none'; // stopped or superseded: stay silent
    logger.warn('AiDE read-aloud unavailable, using the browser voice:', err);
    releasePlayback();
    return speakWithBrowser(spoken, options.lang) ? 'browser' : 'none';
  } finally {
    if (pending === controller) pending = null;
  }
};

const looksLikeJson = (text: string): boolean => {
  const trimmed = text.trim();
  if (!/^[[{]/.test(trimmed)) return false;
  try {
    JSON.parse(trimmed);
    return true;
  } catch {
    return false;
  }
};

/**
 * Prose as a listener should hear it: markup removed, links named rather than
 * spelled out, line breaks turned into pauses. Words, numbers and times are
 * left exactly as written.
 */
export const cleanForSpeech = (text: string): string => {
  if (!text || looksLikeJson(text)) return '';

  const cleaned = text
    .replace(/```[\s\S]*?```/g, ' ')                         // code blocks
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')                 // images → alt text
    .replace(/\[([^\]]+)\]\((?:https?:\/\/|www\.)[^)]*\)/g, '$1') // [label](url) → label
    .replace(/\b(?:https?:\/\/|www\.)\S+/g, 'link')           // bare URLs
    .replace(/`([^`]*)`/g, '$1')                              // inline code
    .replace(/(\*\*|__)(.+?)\1/g, '$2')                       // bold
    .replace(/(^|[\s(])[*_]([^*_\n]+)[*_](?=[\s).,;:!?]|$)/g, '$1$2') // italics
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')                       // headings
    .replace(/^\s{0,3}>\s?/gm, '')                            // quotes
    .replace(/^\s*[-*+•]\s+/gm, '')                           // bullets
    .replace(/^\s*\|?[\s:|-]*-{3,}[\s:|-]*\|?\s*$/gm, '');    // table dividers

  return cleaned
    .split(/\n+/)
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    // A line break is a pause; the last line is left exactly as written.
    .map((line, i, lines) => (i === lines.length - 1 || /[.!?:;,]$/.test(line) ? line : `${line}.`))
    .join(' ');
};

const valueText = (value: unknown): string =>
  value === null || value === undefined ? '' : cleanForSpeech(String(value)).replace(/\.$/, '');

const sentence = (...parts: string[]): string =>
  parts
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => (/[.!?]$/.test(p) ? p : `${p}.`))
    .join(' ');

const labelledItems = (items: Array<[unknown, unknown]>): string => {
  const spoken = items
    .slice(0, MAX_SPOKEN_ITEMS)
    .map(([label, value]) => [valueText(label), valueText(value)].filter(Boolean).join(': '))
    .filter(Boolean);
  const more = items.length - MAX_SPOKEN_ITEMS;
  return sentence([...spoken, ...(more > 0 ? [`and ${more} more`] : [])].join(', '));
};

const tableText = (body: ResponseBody, t?: Translate): string => {
  const columns: unknown[] = Array.isArray(body.columns) ? body.columns : [];
  const rows: unknown[][] = Array.isArray(body.rows) ? body.rows.filter(Array.isArray) : [];
  const title = valueText(body.title);
  const summary = valueText(body.summary);

  if (rows.length === 0) {
    return sentence(title, t ? String(t('alpha.table.noResults')) : 'No results found.', summary);
  }

  if (rows.length === 1) {
    // One record reads naturally field by field — the same pairs the table shows.
    const fields = columns
      .map((column, i) => [valueText(column), valueText(rows[0][i])])
      .filter(([column, value]) => column && value)
      .slice(0, MAX_SPOKEN_FIELDS)
      .map(([column, value]) => `${column}: ${value}`);
    return sentence(title, fields.join(', '), summary);
  }

  // Many records: say what the table is, not every cell. The rows stay on screen.
  const count = t ? String(t('alpha.table.recordCount', { count: rows.length })) : `${rows.length} records`;
  return sentence(title || count, summary);
};

/**
 * The spoken form of one assistant answer, derived from the same body the chat
 * renders (resolveResponseBody) and mirroring each renderer in ChatView: prose
 * as prose, KPI cards and rankings as "label: value", a table as its title,
 * summary and — for a single record — its fields. Never JSON, never metadata
 * such as `data` or `thread_id`.
 */
export const toSpeakableText = (payload: unknown, t?: Translate): string => {
  const body = resolveResponseBody(payload);
  if (!body) return '';

  if (Array.isArray(body.kpis)) {
    return sentence(
      valueText(body.title),
      labelledItems(body.kpis.map((kpi: ResponseBody) => [kpi?.kpi, kpi?.value])),
    );
  }

  switch (body.type) {
    case 'text':
      return cleanForSpeech(String(body.raw || body.text || body.content || body.response || ''));
    case 'kpi':
      return Array.isArray(body.value)
        ? sentence(valueText(body.title), labelledItems(body.value.map((k: ResponseBody) => [k?.title, k?.value])))
        : labelledItems([[body.title, body.value]]);
    case 'topn':
    case 'bar':
      return sentence(
        valueText(body.title),
        labelledItems(
          (Array.isArray(body.rows) ? body.rows : []).map((row: unknown[]) => [row?.[0], row?.[1]]),
        ),
      );
    case 'table':
      return tableText(body, t);
    default:
      if (Array.isArray(body.columns) && Array.isArray(body.rows)) return tableText(body, t);
      return '';
  }
};
